// List of allowed feature flags

// NOTE! Ensure that this list is always alphabetically sorted by key! Do NOT
// throw new feature flags on to the end of this object as that creates merge
// conflicts
export enum FeatureFlag {
  /**
   * Gates the AI incident summary: the Summarize action on the incidents
   * table's selection action bar and the modal that renders the generated
   * digest. While off, no Summarize button appears and the modal is never
   * mounted, so the generateIncidentSummary mutation is never called — which
   * matters because that mutation is itself flag-gated server side and bills a
   * Bedrock call per request.
   *
   * @see {@link https://hivewatch.atlassian.net/browse/ENG-32157|ENG-32157}
   */
  aiIncidentSummary = 'aiIncidentSummary',
  // TODO Update aiOperator based off ENG-17897 FF
  aiOperator = 'aiOperator',
  aiOperatorHub = 'aiOperatorHub',
  aiOperatorReasoningTrail = 'aiOperatorReasoningTrail',
  aioPmHub = 'aioPmHub',
  /**
   * Gates the HiveWatch rebrand re-skin of the application header — the
   * standard header plus the Program Recommendations and AI Operator Hub
   * variants. While off, these headers render in their current styling.
   * Visual re-skin only — no layout, structure, or behavior changes, and all
   * existing instrumentation continues to fire.
   *
   * @see {@link https://hivewatch.atlassian.net/browse/ENG-30681|ENG-30681}
   */
  appHeaderRebrand = 'appHeaderRebrand',
  /**
   * Gates the archive UX for facilities and floors. When on, the facility
   * delete flow presents as a soft-delete archive (cascading to floors,
   * floor plans, and devices) and the Edit Floor modal shows an Archive
   * Floor action that calls the archiveFloor mutation. Backend support
   * shipped with ENG-29678.
   * @see {@link https://hivewatch.atlassian.net/browse/ENG-29678|ENG-29678}
   */
  archiveFloorAndFacility = 'archiveFloorAndFacility',
  attachMediaToIncidentNote = 'attachMediaToIncidentNote',
  authenticatedProxyStreaming = 'authenticatedProxyStreaming',
  /**
   * Adds a Delete action to the dark devices table's FAB, allowing an
   * Implementer to delete every selected device in one pass. Off by default. the action
   * is additionally gated on `shouldEnableDeleteDevice`, so it is never more permissive
   * than single-device delete. Requires `deviceAssociationStatus` (the dark table) and
   * `bulkEditDevices` (the checkbox column) to be on to be reachable at all — nothing
   * enforces that chain, so enable all three together in NEXT_PUBLIC_FEATURE_FLAGS.
   *
   * @see {@link https://hivewatch.atlassian.net/browse/ENG-32778}
   */
  bulkDeleteDevices = 'bulkDeleteDevices',
  bulkEditDevices = 'bulkEditDevices',
  bulkIncidentActions = 'bulkIncidentActions',
  /**
   * Gates the "Set stream account" bulk action on the dark Devices table FAB, which bulk-sets
   * `streamingType` + `externalAccountId` on selected cameras via one UpdateCamera call each.
   * Additionally gated on `usePermissions().shouldShowExternalAccountFields` (implementer-only),
   * so enabling this flag alone does not expose external accounts to PMs.
   *
   * Depends on two other flags, and is inert without both:
   * - `deviceAssociationStatus` — the action lives only on `DarkDevicesTable`, and `devices.tsx`
   *   renders the legacy `PaginatedDevicesTable` instead while that flag is off.
   * - `bulkEditDevices` — gates the row checkboxes and the FAB that hosts the action, so with it
   *   off there is no selection to act on.
   */
  bulkSetExternalAccount = 'bulkSetExternalAccount',
  cameraTestUtil = 'cameraTestUtil', // Utility to Test camera config in Edit Camera modal. This has nothing to do with testing code.
  camioCameraStream = 'camioCameraStream',
  cannedDashboardBarCartData = 'cannedDashboardBarCartData',
  cannedVideo = 'cannedVideo',
  caseManagement = 'caseManagement',
  /**
   * Feature flag to control the blurred clip preview while we wait for an Incident's clip to come in.
   * Please note that this is a heavy process as it requires pulling in an entire other clip.
   *
   * @see {@link https://hivewatch.atlassian.net/browse/ENG-30963|ENG-30963}
   */
  clipLoaderPreview = 'clipLoaderPreview',
  customerSecretProvider = 'customerSecretProvider',
  customQuickReport = 'customQuickReport',
  dashboardHome = 'dashboardHome',
  dashboardOITableFilters = 'dashboardOITableFilters',
  dashboardOITableIncidentTypeColumn = 'dashboardOITableIncidentTypeColumn',
  development = 'development',
  deviceAssociationStatus = 'deviceAssociationStatus',
  deviceHealthSettings = 'deviceHealthSettings',
  deviceLabeling = 'deviceLabeling',
  downloadableIncidentVideos = 'downloadableIncidentVideos',
  embeddedDashboard = 'embeddedDashboard',
  emergencyCall = 'emergencyCall',
  /**
   * Gates the HiveWatch rebrand re-skin of everything under a facility. Started
   * as the Facilities page (paginated path); now also gates the floor-plan Tour
   * Plans rail, which is rebuilt on the design system behind this flag and is
   * the only rail on the Design Library Side Panel Sm width. The other three
   * rails (Devices, Floors, Checkpoints) are untouched until their own tickets
   * migrate them. While off, every facility surface renders in its current
   * styling. Visual re-skin only — no behavior changes, and all existing
   * instrumentation fires unchanged in both branches.
   *
   * @see {@link https://hivewatch.atlassian.net/browse/ENG-30676|ENG-30676}
   * @see {@link https://hivewatch.atlassian.net/browse/ENG-32001|ENG-32001}
   */
  facilitiesRebrand = 'facilitiesRebrand',
  // Gates the FTU engine as a whole; individual tours carry their own
  // featureFlag in FTU_REGISTRY (e.g. videoWall, showDarkModeToggle).
  firstTimeUse = 'firstTimeUse',
  /**
   * When a device is dropped onto the floor plan, also place its unplaced directly associated
   * devices (tailgate connections) in a fan around the drop point, and show a toast explaining
   * why the extra devices appeared. Without this, a user can drag out an associated device and
   * see nothing on the floor plan reflecting that association.
   */
  floorPlanAssociatedDevicePlacement = 'floorPlanAssociatedDevicePlacement',
  /**
   * Gates the HiveWatch rebrand re-skin of the Floor-plan page header
   * (breadcrumb, floor dropdown, and the four side-panel toggle buttons).
   * While off, the Floor-plan header renders in its current styling.
   * Visual re-skin only — no layout, structure, or behavior changes, and all
   * existing instrumentation continues to fire unchanged.
   *
   * @see {@link https://hivewatch.atlassian.net/browse/ENG-30683|ENG-30683}
   */
  floorPlanHeaderRebrand = 'floorPlanHeaderRebrand',
  /**
   * Gates the HiveWatch rebrand re-skin of the Gateways page. While off, the
   * Gateways page renders in its current styling.
   *
   * @see {@link https://hivewatch.atlassian.net/browse/ENG-30679|ENG-30679}
   */
  gatewaysTableRebrand = 'gatewaysTableRebrand',
  guardTours = 'guardTours',
  guardToursV2 = 'guardToursV2',
  incidentAudioAlerts = 'incidentAudioAlerts',
  /**
   * Gates the Incident Drawer Map Panel: a left-hand slide-out panel of the
   * dark (rebrand) incident drawer showing an interactive Mapbox satellite
   * map centered on the incident's facility. While off, neither the panel nor
   * its trigger button next to the location breadcrumb renders. Dark drawer
   * tree only; requires incidentDrawerRebrand to be reachable.
   */
  incidentDrawerMapPanel = 'incidentDrawerMapPanel',
  incidentDrawerRebrand = 'incidentDrawerRebrand',
  incidentResponseWorkflow = 'incidentResponseWorkflow',
  incidentRules = 'incidentRules',
  /**
   * Migrates the account-settings "Incident Categories" section to the unified
   * incidentTypes query and renames it to "Incident Types". Off keeps the
   * manualIncidentCategories-backed behavior untouched.
   *
   * @see {@link https://hivewatch.atlassian.net/browse/ENG-32275|ENG-32275}
   * @see {@link https://hivewatch.atlassian.net/browse/ENG-32026|ENG-32026}
   */
  incidentTypeDefaultPriority = 'incidentTypeDefaultPriority',
  ipv6Redirection = 'ipv6Redirection',
  legacyDashboard = 'legacyDashboard',
  llmProcessedIncidents = 'llmProcessedIncidents',
  /**
   * Gates live view for Lumana-backed cameras in the Device View drawer:
   * renders the LiveKitVideoPlayer (browser ↔ Lumana cloud WebRTC via
   * livekit-client) when `streamingUrlWrapper` returns the LiveKit variant.
   * While off — or for any non-Lumana camera — the existing HLS/MSE players
   * render exactly as today, so it is a safe no-op. Per-VMS flag following
   * the camioCameraStream / orangePeelCameraStream precedent.
   *
   * @see {@link https://hivewatch.atlassian.net/browse/ENG-31640|ENG-31640}
   */
  lumanaCameraStream = 'lumanaCameraStream',
  orangePeelCameraStream = 'orangePeelCameraStream',
  orderRepair = 'orderRepair',
  pacsSourceSecrets = 'pacsSourceSecrets',
  /**
   * Gates the Priority Queue quick filter on the incidents table. Also
   * reused (per product decision) to gate the Critical-priority incident
   * sound bypass: while on, a Critical incident plays its alert sound for
   * every logged-in operator regardless of the per-user audio-alerts
   * setting, the notifications mute toggle, and the active incidents-table
   * filter. While off, Critical incidents are subject to the same gates as
   * every other priority.
   *
   * @see {@link https://hivewatch.atlassian.net/browse/ENG-30964|ENG-30964}
   * @see {@link https://hivewatch.atlassian.net/browse/ENG-31918|ENG-31918}
   */
  priorityQueue = 'priorityQueue',
  quickReport = 'quickReport',
  quickReportWhitelist = 'quickReportWhitelist',
  recommendationsEngine = 'recommendationsEngine',
  /**
   * Removes the edit-mode requirement from creation flows — the first step in
   * retiring the global edit-mode toggle entirely.
   *
   * While on, items in the Create dropdown (Facility, Floor, Device, SOP,
   * Gateway, PACS, Rule, Automation) are gated on the user's permissions
   * alone, so an Implementer/PM can create without first flipping edit mode.
   * While off, creation keeps requiring both the permission and edit mode, so
   * it is a safe no-op.
   */
  removeEditMode = 'removeEditMode',
  /**
   * Gates playing the resolved incident clip (ORIGINAL/DOWNSCALED, per api-general's resolution
   * order) selected from `Incident.clips` in the existing incident player. Falls back to
   * `Incident.video` when no resolved clip is available, so it is a safe no-op while off.
   *
   * @see {@link https://hivewatch.atlassian.net/browse/ENG-30186|ENG-30186}
   */
  resolvedIncidentClip = 'resolvedIncidentClip',
  /**
   * Gates the HiveWatch rebrand re-skin of the Rules table view. While off,
   * the Rules page table/search/filter area renders in its current styling.
   * Header ownership remains with the appHeaderRebrand work.
   *
   * @see {@link https://hivewatch.atlassian.net/browse/ENG-30680|ENG-30680}
   */
  rulesTableViewRebrand = 'rulesTableViewRebrand',
  salesDatabaseReset = 'salesDatabaseReset',
  // Currently only used to allow searching for a Facility in Create Incident modal.
  // Longer term we should make this look better, and make it the default UX
  searchFacilityByAddress = 'searchFacilityByAddress',
  sendToOntic = 'sendToOntic',
  /**
   * Gates the rebuilt Settings information architecture and shared page shell.
   * While off, the existing account-management page renders unchanged.
   *
   * @see {@link https://hivewatch.atlassian.net/browse/ENG-33336|ENG-33336}
   */
  settingsRebrand = 'settingsRebrand',
  /**
   * The single gate on dark mode, and the widest-reaching flag in the app: it
   * controls both the Dark Mode toggle in Account settings and whether
   * `useLightMode()` can resolve to `'dark'` at all. Dark is the default, so
   * turning this on makes the whole app dark for everyone who has not opted
   * out — it does not merely reveal a toggle.
   *
   * Off is the escape hatch: no toggle, and a light app. Use it where the other
   * rebrand flags are not all on, so the toggle can never appear to do nothing.
   *
   * @see {@link https://hivewatch.atlassian.net/browse/ENG-33713|ENG-33713}
   */
  showDarkModeToggle = 'showDarkModeToggle',
  showDevicePageUnresolvedIncidents = 'showDevicePageUnresolvedIncidents',
  /**
   * Gates the bulk resolve UI inside the team stale work modal (ENG-32272):
   * card checkboxes with per-group select-all, per-person "Resolve all"
   * buttons, and the PM/implementer-only program-wide resolve button, all
   * confirmed through a note-entry modal backed by the
   * bulkResolveStaleIncidents mutation. Only meaningful with
   * staleWorkBanner on (the modal it augments never renders otherwise).
   * While off, the modal keeps its read-only ENG-32131 behavior.
   */
  staleBulkResolve = 'staleBulkResolve',
  /**
   * Gates the stale work banner: an app-wide banner showing the logged-in
   * operator how many incidents they acknowledged over an hour ago that are
   * still unresolved, with a modal listing them as cards that open the
   * incident drawer. While off, no banner renders and the stale-work count
   * query never fires.
   */
  staleWorkBanner = 'staleWorkBanner',
  traceLogs = 'traceLogs',
  traceLogsPii = 'traceLogsPii',
  updatedDeviceBulkUpload = 'updatedDeviceBulkUpload',
  updatedRules = 'updatedRules',
  useEventPromotionAlarms = 'useEventPromotionAlarms',
  useIncidentTypeDefinition = 'useIncidentTypeDefinition',
  usePaginatedFacilityTable = 'usePaginatedFacilityTable',
  userManagementV1 = 'userManagementV1',
  // SHOULD NOT BE ENABLED IN ANY REAL ENV YET!
  videoWall = 'videoWall',
}

type FlagEntries = Array<[FeatureFlag, 'true' | 'false']>

export type Flags = {
  [key in FeatureFlag]?: boolean
}

const parseFlagEntries = (flagEntries: FlagEntries): Flags => {
  const flags = {}
  for (const [name, value] of flagEntries) {
    switch (value) {
      case 'true':
        flags[name] = true
        break
      case 'false':
        flags[name] = false
        break
      default:
        throw new Error(`incorrect value: ${value} for flag ${name}`)
    }
  }
  return flags
}

const flagEntries = process.env.NEXT_PUBLIC_FEATURE_FLAGS
  ? process.env.NEXT_PUBLIC_FEATURE_FLAGS?.split(',').map((flagString) =>
      flagString.split(':')
    )
  : []

const FEATURE_FLAGS = parseFlagEntries(flagEntries as FlagEntries)

export const useFeatureFlag = (flag: FeatureFlag): boolean => {
  return FEATURE_FLAGS?.[flag] || false
}

// TODO - Should be renamed to something like getFeatureFlags?
export const useFeatureFlags = (): Flags => {
  return FEATURE_FLAGS
}

type ProviderType = ({ children }: { children: React.ReactNode }) => JSX.Element

export const flagProvider = (provider: ProviderType, flag: FeatureFlag) => {
  const isEnabled = useFeatureFlag(flag)
  return isEnabled ? provider : ({ children }) => children
}
